import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";

import store from "utils/store";
import FullPageLoader from "shared/FullPageLoader";
import ToastRenderer from "shared/ReduxToast";

import App from "pages";
import * as serviceWorker from "./serviceWorker";

import "sanitize.css/sanitize.css";
import "@druva-apps/orion/lib/styles/orion.css";
import { BASENAME } from "consts";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));

const app = (
  <BrowserRouter basename={BASENAME}>
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools />
        <App />
        <ToastRenderer />
        <FullPageLoader />
      </QueryClientProvider>
    </Provider>
  </BrowserRouter>
);

const useMsw =
  process.env.NODE_ENV === "development" &&
  process.env.REACT_APP_ENABLE_MSW === "true";

/** Always paint React first; MSW can finish activating in the background. */
root.render(app);

if (useMsw) {
  /**
   * Do NOT call serviceWorker.unregister() while MSW is enabled — it races
   * with worker.start() via navigator.serviceWorker.ready.
   */
  void (async () => {
    try {
      const { worker } = await import("./mocks/worker");
      await worker.start({
        serviceWorker: {
          url: `${process.env.PUBLIC_URL || ""}/mockServiceWorker.js`,
        },
        onUnhandledRequest: "bypass",
        /**
         * Default `waitUntilReady: true` waits for SW activation; a mismatched
         * public/mockServiceWorker.js (wrong INTEGRITY_CHECKSUM) never
         * activates, so start() never resolves and the UI stayed blank when we
         * rendered after start(). Keep false so mocks attach as soon as possible.
         */
        waitUntilReady: false,
        quiet: true,
      });
      console.info("[MSW] Mocking enabled");
    } catch (err) {
      console.error("[MSW] Failed to start:", err);
    }
  })();
} else {
  serviceWorker.unregister();
}
